import {Injectable, Injector} from '@angular/core';
import {Params} from '@angular/router';
import {map, of, pipe, switchMap, tap} from 'rxjs';

import {Selector, State, StateContext, StateToken} from '@ngxs/store';
import {patch} from '@ngxs/store/operators';
import {EmitterAction, Receiver} from '@ngxs-labs/emitter';
import {Navigate} from '@ngxs/router-plugin';

import {
  FlowModelResponse,
  FlowPropertiesResponse,
  FlowStartedResponse,
  NextFlowResponse
} from 'frontend-flow-api/model/models';
import {FrontendFlowService} from 'frontend-flow-api/api/api';
import {
  AccountWithBalance,
  FrontendExtendedConfiguration,
  PaymentProvider,
  PaymentProviderList,
  Transaction,
  UserAccounts
} from 'frontend-data-api/model/models';
import {FrontendDataService} from 'frontend-data-api/api/api';

import {UrlListEnum} from "../core/url-list.enum";
import {ERROR_STATUSES} from "../core/errors-def";

export const VARIABLE_SYMBOL = 'variable_symbol';
export const MERCHANT_SHOPPING_CART_URL = 'merchant_shopping_cart_url';
export const MERCHANT_REDIRECT_URL = 'merchant_redirect_url';
export const INITIATOR_NAME = 'initiator_name';
export const STEP_BACK_SELECTED = 'step_back_selected';
export const STEP_BACK_TO_ESHOP_CART_SELECTED = 'selected_back_to_eshop_cart';
export const REDIRECT_CONFIRMED = 'redirect_confirmed';

interface DataStateModel {
  language: string;
  token: string;
  flowId: string;
  sessions: { [flowId: string]: string };
  selectedPaymentProvider: string;
  selectedAccount: AccountWithBalance;
  paymentProviderList: PaymentProviderList;
  accounts: Array<AccountWithBalance>;
  transaction: Transaction;
  flow: NextFlowResponse;
  flowModel: { [key: string]: string | undefined; };
  flowProperties: FlowPropertiesResponse;
  configuration: FrontendExtendedConfiguration;
  errorCode: number;
}

export class GetProperties {
  static readonly type = '[API] Get properties'
}

export const WHITEBRICKS_STATE_TOKEN = new StateToken<string>('whitebricks');

@State<Partial<DataStateModel>>({
  name: WHITEBRICKS_STATE_TOKEN,
  defaults: {
    language: undefined,
    token: undefined,
    flowId: undefined,
    sessions: {},
    selectedPaymentProvider: undefined,
    selectedAccount: undefined,
    paymentProviderList: {},
    accounts: undefined,
    transaction: {},
    flow: {},
    flowModel: {},
    flowProperties: {},
    configuration: undefined
  }
})
@Injectable()
export class DataState {
  private static frontendFlowService: FrontendFlowService;
  private static frontendDataService: FrontendDataService;

  constructor(private readonly injector: Injector) {
    DataState.frontendFlowService = this.injector.get<FrontendFlowService>(FrontendFlowService);
    DataState.frontendDataService = this.injector.get<FrontendDataService>(FrontendDataService);
  }

  @Selector()
  static redirectUrlForSpecificErrorCodes(state: DataStateModel): string | undefined {
    return state.flowProperties?.properties && (state.flowProperties.properties[MERCHANT_SHOPPING_CART_URL] || state.flowProperties.properties[MERCHANT_REDIRECT_URL]);
  }

  @Selector()
  static merchantShoppingCartRedirectUrl(state: DataStateModel): string | undefined {
    return state.flowProperties?.properties && state.flowProperties.properties[MERCHANT_SHOPPING_CART_URL];
  }

  @Selector()
  static merchantRedirectUrl(state: DataStateModel): string | undefined {
    return state.flowProperties?.properties && state.flowProperties.properties[MERCHANT_REDIRECT_URL];
  }

  @Selector()
  static variableSymbol(state: DataStateModel): string | undefined {
    return state.flowProperties?.properties && state.flowProperties.properties[VARIABLE_SYMBOL];
  }

  @Selector()
  static termsAndConditionsLink(state: DataStateModel): string | undefined {
    return state.configuration.cdnBaseUrl + '/terms/actual/services_inside_cz/' + state.language + '/terms_and_conditions_ais_pis.pdf';
  }

  @Selector()
  static merchantName(state: DataStateModel) {
    return state.flowProperties.properties &&
    state.flowProperties.properties[INITIATOR_NAME] ? state.flowProperties.properties[INITIATOR_NAME] : state.configuration.merchantDetail?.merchantName;
  }

  @Selector()
  static selectedBank(state: DataStateModel): PaymentProvider | undefined {
    return state.paymentProviderList!.paymentProviders?.find(pp => pp.code === state.selectedPaymentProvider);
  }

  @Receiver()
  static saveLanguage(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<string>) {
    ctx.patchState({language: payload});
  }

  @Receiver({type: '[API] Request token, redirect.'})
  static startFlow(ctx: StateContext<DataStateModel>) {
    const flowId = ctx.getState().flowId;
    return this.frontendFlowService.startFlow(flowId).pipe(
      tap((response: FlowStartedResponse) => {
        const token = response.token;
        ctx.patchState({token});

        if (token) {
          ctx.setState(
            patch<DataStateModel>({
              sessions: patch({
                [flowId]: token
              })
            }));
        }

      }),
      switchMap(() => this.frontendFlowService.getActualState(ctx.getState().flowId)
        .pipe(
          saveFlowAndRedirect(ctx, DataState.redirectToPage)
        ))
    );
  }

  @Receiver({type: '[API] Save flowId from URL and redirect'})
  static redirectToActualPage(ctx: StateContext<DataStateModel>) {
    return this.frontendFlowService.getActualState(ctx.getState().flowId).pipe(
      saveFlowAndRedirect(ctx, DataState.redirectToPage)
    )
  }

  @Receiver()
  static changeScreenPath(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<string>) {
    ctx.patchState({flow: {screenPath: payload}});
  }

  @Receiver()
  static setErrorCode(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<number>) {
    ctx.patchState({errorCode: payload});
  }

  @Receiver()
  static checkAndRedirectToActualPage(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<string>) {
    if (!ctx.getState().flowId) {
      ctx.dispatch(new Navigate(['/error']));
      return of(undefined);
    } else {
      return this.frontendFlowService.getActualState(ctx.getState().flowId).pipe(
        tap((response: NextFlowResponse) => {
          ctx.patchState({flow: response});
          if (ctx.getState().flow.screenPath === 'undefined' || (payload !== ctx.getState().flow.screenPath && !ERROR_STATUSES.includes(ctx.getState().errorCode))) {
            DataState.redirectToPage(ctx)
          }
        })
      )
    }
  }

  @Receiver({type: '[Bank selection page] Bank selected'})
  static bankSelected(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<string>) {
    ctx.patchState({selectedPaymentProvider: payload});
    return this.frontendFlowService.patchStepModel(ctx.getState().flowId, {model: {"payment_provider": payload}}).pipe(
      switchMap(() => this.frontendFlowService.postFlowEvent(ctx.getState().flowId, {event: 'payment_provider_selected'}).pipe(
        saveFlowAndRedirect(ctx, DataState.redirectToPage)
      ))
    );
  }

  @Receiver({type: '[Account selection page] Account selected'})
  static accountSelected(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<AccountWithBalance>) {
    ctx.patchState({selectedAccount: payload});
    return this.frontendFlowService.patchStepModel(
      ctx.getState().flowId,
      {model: {iban: payload?.identification?.iban, owner_name: payload?.ownersNames?.toLocaleString()}}
    ).pipe(
      switchMap(() => this.frontendFlowService.postFlowEvent(ctx.getState().flowId, {event: 'account_selected'}).pipe(
        saveFlowAndRedirect(ctx, DataState.redirectToPage)
      ))
    )
  }

  @Receiver({type: '[Account confirmed page] Pay button pressed'})
  static accountConfirmed(ctx: StateContext<DataStateModel>) {
    return this.frontendFlowService.postFlowEvent(ctx.getState().flowId, {event: 'confirm_account_selection'}).pipe(
      saveFlowAndRedirect(ctx, DataState.redirectToPage)
    );
  }

  @Receiver()
  static getBanks({getState, patchState}: StateContext<DataStateModel>) {
    const flowId = getState().flowId;
    let authToken = getState().token;
    if (!authToken) {
      const sessions = getState().sessions;
      authToken = sessions[flowId];
    }
    return this.frontendDataService.paymentProvidersGet(flowId, authToken).pipe(
      tap((banks: PaymentProviderList) => patchState({paymentProviderList: banks})));
  }

  @Receiver()
  static getSelectedBank({getState, patchState}: StateContext<DataStateModel>) {
    return this.frontendFlowService.getStepModel(getState().flowId).pipe(
      tap((response: FlowModelResponse) => patchState({
        flowModel: response.model,
        selectedPaymentProvider: response.model!['payment_provider']
      }))
    )
  }

  @Receiver()
  static getAccounts({getState, patchState}: StateContext<DataStateModel>) {
    return this.frontendDataService.usersAccountsGet(getState().flowId).pipe(
      map((userAccounts: UserAccounts) => userAccounts?.accountWithBalance),
      tap((accounts) => patchState({accounts: accounts}))
    );
  }

  @Receiver()
  static getTransaction({getState, patchState}: StateContext<DataStateModel>) {
    return this.frontendDataService.transactionDetailGet(getState().flowId).pipe(
      tap((trans: Transaction) => patchState({transaction: trans}))
    )
  }

  @Receiver({type: '[Transition screen] Redirect Confirmed'})
  static redirectToNextPageConfirmed(ctx: StateContext<DataStateModel>) {
    return this.frontendFlowService.postFlowEvent(ctx.getState().flowId, {event: REDIRECT_CONFIRMED})
      .pipe(
        saveFlowAndRedirect(ctx, DataState.redirectToPage)
      );
  }

  @Receiver({type: '[App Guard class] Back to previous page'})
  static backToPreviousPage(ctx: StateContext<DataStateModel>) {
    return this.frontendFlowService.postFlowEvent(ctx.getState().flowId, {event: STEP_BACK_SELECTED})
      .pipe(
        saveFlowAndRedirect(ctx, DataState.redirectToPage)
      );
  }

  @Receiver({type: '[App Guard class] Back to eshop cart'})
  static backToEshop(ctx: StateContext<DataStateModel>) {
    return this.frontendFlowService.postFlowEvent(ctx.getState().flowId, {event: STEP_BACK_TO_ESHOP_CART_SELECTED})
      .pipe(
        saveFlowAndRedirect(ctx, DataState.redirectToPage)
      );
  }

  @Receiver({type: '[Error Page] Redirect back to select banks'})
  static redirectOnErrorContinueSelectBanks(ctx: StateContext<DataStateModel>) {
    return ctx.dispatch(new Navigate(['/' + UrlListEnum.PAGE_PAYMENT_PROVIDER_SELECTION]));
  }

  @Receiver({type: '[Error Page] Redirect to merchant'})
  static redirectOnErrorContinueToMerchant(ctx: StateContext<DataStateModel>) {
    window.open(ctx.getState().flowProperties.properties![MERCHANT_SHOPPING_CART_URL], '_self')
  }

  private static redirectToPage(ctx: StateContext<DataStateModel>): void {
    if (ctx.getState().flow.screenPath) { //in case empty space it will result in error page via routing TODO je to v poradku?
      ctx.dispatch([GetProperties, DataState.navigateWithParsedParams(ctx.getState().flow.screenPath)]);
    } else {
      window.open(ctx.getState().flow.redirectUrl, '_self');
    }
  }

  private static navigateWithParsedParams(path: string | undefined): Navigate {
    const params: Params = {};
    let pathWithoutParams = path;
    if (path && path.includes('error')) {
      const pathQuery = path.split('?');
      pathWithoutParams = pathQuery[0];
      pathQuery[1].split('&').forEach(x => {
        const keyValue = x.split('=');
        params[keyValue[0]] = keyValue[1];
      });

    }
    return new Navigate([pathWithoutParams], params);
  }

  @Receiver()
  static getFEConfigurationOnStart(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<string>) {
    return this.frontendDataService.configurationExtendedFrontendGet(payload).pipe(
      tap(response => ctx.patchState({configuration: response}))
    )
  }

  @Receiver({action: GetProperties})
  static getProperties({getState, patchState}: StateContext<DataStateModel>) {
    return this.frontendFlowService.getFlowProperties(getState().flowId).pipe(
      tap(response => patchState({flowProperties: response}))
    )
  }

  @Receiver()
  static saveFlowId(ctx: StateContext<DataStateModel>, {payload}: EmitterAction<string>) {
    ctx.patchState({flowId: payload});
  }
}

function saveFlowAndRedirect(ctx: StateContext<DataStateModel>, fnc: (ctx: StateContext<DataStateModel>) => void) {
  return pipe(tap((response: NextFlowResponse) => {
      ctx.patchState({flow: response});
      fnc(ctx);
    })
  );
}
